import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PenLine } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SignaturePad } from '@/components/ui/SignaturePad';
import { useSignDocument, type DocumentRow } from '@/hooks/data/useDocuments';
import { humanizeApiError } from '@/lib/errorMessages';

interface Props {
  document: DocumentRow;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSigned?: () => void;
}

const TYPE_TITLE: Record<DocumentRow['type'], string> = {
  vermittlungsauftrag: 'Allein-Vermittlungsauftrag',
  besichtigungsschein: 'Besichtigungsschein',
  kaufanbot: 'Befristetes Kaufanbot',
};

/** Migration 0058 — two contracts share the `vermittlungsauftrag` type. Only an
 *  explicit 'einfach' changes the wording; everything else is exclusive. */
function documentTitle(doc: DocumentRow): string {
  if (doc.type === 'vermittlungsauftrag' && doc.contract_variant === 'einfach') {
    return 'Vermittlungsauftrag';
  }
  return TYPE_TITLE[doc.type];
}

export function SignDocumentDialog({ document, open, onOpenChange, onSigned }: Props) {
  const { t } = useTranslation();
  const sign = useSignDocument();
  const [pending, setPending] = useState(false);

  const onCapture = async (base64: string) => {
    setPending(true);
    try {
      await sign.mutateAsync({ id: document.id, imageBase64: base64 });
      toast.success(`${documentTitle(document)} Nr. ${document.number} unterschrieben.`);
      onSigned?.();
      onOpenChange(false);
    } catch (cause) {
      // 403 since 2026-09-21: signing now makes a seller exclusive, so only
      // the people working the document may record it.
      toast.error(humanizeApiError((cause as Error).message, t, t('errors.general', 'Ein Fehler ist aufgetreten.')));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <PenLine className="h-4 w-4" />
            {t('signature.title', 'Dokument unterschreiben')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-line bg-paper-2 px-3 py-2 text-sm">
            <p className="font-medium text-ink">{documentTitle(document)}</p>
            <p className="text-xs text-ink-3">
              {t('signature.doc_number', 'Dokument-Nr.')}:{' '}
              <span className="font-mono">{document.number}</span>
            </p>
          </div>
          <SignaturePad
            onCapture={onCapture}
            onCancel={() => onOpenChange(false)}
            disabled={pending}
          />
          <p className="text-[11px] text-ink-3">
            {t(
              'signature.legal_note',
              'Mit dem Bestätigen bestätigen Sie, dieses Dokument elektronisch unterschrieben zu haben. Datum und Uhrzeit werden automatisch protokolliert.',
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
